import { FC, useState } from "react"
import { Building2, AlertTriangle } from "lucide-react"
import { Brand, LoanOfficer } from "../../../../types"
import BrandAutocomplete from "../../brand-autocomplete"
import LoanOfficerAutocomplete from "../../loan-officer-autocomplete"
import { PrimaryBtn } from "../../buttons"
import Dialog from "../../dialog"
import loTransfer from "../../../../api/lo-transfer"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"

interface Props {
  brands: Brand[]
}

const LoTransferBrand: FC<Props> = ({ brands }) => {
  const dispatch = useAppDispatch()
  const [currentBrandId, setCurrentBrandId] = useState<string>("")
  const [currentBrandName, setCurrentBrandName] = useState<string>("")
  const [loid, setLoid] = useState<string>("")
  const [assetRecipientLoid, setAssetRecipientLoid] = useState<string>("")
  const [newBrandId, setNewBrandId] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const canTransfer =
    currentBrandId &&
    loid &&
    assetRecipientLoid &&
    newBrandId &&
    currentBrandId !== newBrandId &&
    loid !== assetRecipientLoid

  const handleTransfer = async () => {
    setShowConfirm(false)
    setIsLoading(true)

    try {
      // Step 1: Transfer assets to recipient LO
      await loTransfer.transferAssets({
        from_loid: parseInt(loid, 10),
        to_loid: parseInt(assetRecipientLoid, 10)
      })

      // Step 2: Transfer LO to new brand
      await loTransfer.transferBrand({
        loid: parseInt(loid, 10),
        new_brand_id: parseInt(newBrandId, 10)
      })

      dispatch(
        pushToast({
          msg: "Assets transferred and loan officer moved to new brand successfully",
          theme: "success",
          timeout: 4000
        })
      )

      // Reset form
      setCurrentBrandId("")
      setCurrentBrandName("")
      setLoid("")
      setAssetRecipientLoid("")
      setNewBrandId("")
    } catch (error: any) {
      dispatch(
        pushToast({
          msg: error?.message || "Failed to complete transfer",
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleLoSelect = (lo: LoanOfficer) => {
    setCurrentBrandId(lo.bank_nid)
    // Look up brand name from brands list since API doesn't always return it
    const brand = brands.find(b => b.id === lo.bank_nid)
    setCurrentBrandName(brand?.name ?? lo.brand_name ?? "")
    setAssetRecipientLoid("")
    setNewBrandId("")
  }

  const handleLoidChange = (val: string) => {
    setLoid(val)
    if (!val) {
      setCurrentBrandId("")
      setCurrentBrandName("")
    }
    setAssetRecipientLoid("")
    setNewBrandId("")
  }

  const targetBrand = brands.find(b => b.id === newBrandId)

  return (
    <div className="bg-white border border-neutral-200 rounded-lg">
      <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              LO Brand Transfer
            </h3>
            <p className="text-sm text-gray-500">
              Move a loan officer to a different brand
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">
              Assets must stay with the current brand
            </p>
            <p className="mt-1 text-amber-700">
              Before transferring, all assets (leads, SMS threads, etc.) will be
              reassigned to another loan officer in the current brand.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loan Officer to Transfer:
            </label>
            <LoanOfficerAutocomplete
              selected={loid}
              label=""
              direction="vertical"
              showBrandName
              showSearchToggle
              onChange={handleLoidChange}
              onSelectLo={handleLoSelect}
            />
          </div>

          {currentBrandId && (
            <>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Current Brand:</span>{" "}
                  {currentBrandName}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transfer Assets To:
                </label>
                <LoanOfficerAutocomplete
                  searchParams={{ brand_id: currentBrandId }}
                  selected={assetRecipientLoid}
                  label=""
                  direction="vertical"
                  excludeIds={[loid]}
                  onChange={setAssetRecipientLoid}
                />
                <p className="mt-1 text-xs text-gray-500">
                  This LO will receive all assets from the departing loan
                  officer
                </p>
              </div>
            </>
          )}

          {assetRecipientLoid && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Brand:
              </label>
              <BrandAutocomplete
                selected={newBrandId}
                brands={brands.filter(b => b.id !== currentBrandId)}
                direction="vertical"
                onChange={setNewBrandId}
              />
            </div>
          )}
        </div>

        <div className="pt-2">
          <PrimaryBtn
            onClick={() => setShowConfirm(true)}
            disabled={!canTransfer || isLoading}
            className="w-full justify-center"
          >
            {isLoading ? "Transferring..." : "Transfer to Brand"}
          </PrimaryBtn>
        </div>
      </div>

      <Dialog
        open={showConfirm}
        title="Confirm Brand Transfer"
        body={`This will first transfer all assets to the selected loan officer, then move the departing LO from "${
          currentBrandName || "current brand"
        }" to "${targetBrand?.name || "new brand"}". This cannot be undone.`}
        okLabel="Yes, Transfer"
        cancelLabel="Cancel"
        onOk={handleTransfer}
        onCancel={() => setShowConfirm(false)}
        onClose={() => setShowConfirm(false)}
        theme="warning"
      />
    </div>
  )
}

export default LoTransferBrand
